import React, {useState, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import {EditorState, convertFromRaw, convertToRaw} from 'draft-js';
import {draftToMarkdown, markdownToDraft} from 'markdown-draft-js';
import Avatar from 'react-avatar';
import * as yup from 'yup';
import cx from 'classnames';

import {postComment} from '../api/api';
import DraftEditor from './DraftEditor';
import {useAppContext} from '../hooks';
import Link from './Link';

export const commentSchema = yup.object().shape({
  text: yup.string().min(40, 'Comments should be substantive. Is there more you can add?'),
});

// Create a unique draft key for every draft comment, including comment replies
const getDraftKey = (questionId, parentId) => `draftComment-${questionId}${parentId ? `-${parentId}` : ''}`;

const toEditorState = (text) => EditorState
  .createWithContent(convertFromRaw(markdownToDraft(text, {preserveNewlines: true})));

const toText = (editorState) => draftToMarkdown(
  convertToRaw(editorState.getCurrentContent()),
  {preserveNewlines: true},
);

const ClosedCommentEditor = ({onOpen, placeholderText}) => {
  const {currentUser} = useAppContext();

  return (
    <button type="button" onClick={onOpen}>
      <div className="flex items-center p-5">
        <Avatar
          className="comment-profile-img"
          name={currentUser?.displayName}
          src={currentUser?.profile_img}
          color="#f0f0f0"
          fgColor="#000000"
          size={40}
        />
        <div className="text-base text-gray-400">
          {placeholderText}
        </div>
      </div>
    </button>
  );
};

const OpenCommentEditor = ({
  questionID,
  parentCommentID,
  onSubmit,
  placeholderText,
  showToolbar,
}) => {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const {currentUser} = useAppContext();
  const editorRef = useRef();

  useEffect(() => {
    const draftKey = getDraftKey(questionID, parentCommentID);
    const localStorageDraft = JSON.parse(localStorage.getItem(draftKey));

    if (localStorageDraft?.data) {
      setEditorState(toEditorState(localStorageDraft.data));
    }
  }, []);

  const onEditorChange = (editor) => {
    const currentContentState = editorState.getCurrentContent();
    const newContentState = editor.getCurrentContent();

    // Prevent localStorage from being overwritten before useEffect is called
    if (currentContentState !== newContentState) {
      localStorage.setItem(getDraftKey(questionID, parentCommentID),
        JSON.stringify({
          time: Date.now(),
          data: toText(editor),
        }));
    }

    setEditorState(editor);
    setError(null);
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      setSubmitting(true);
      const comment = {text: toText(editorState)};
      if (!parentCommentID) await commentSchema.validate(comment);
      await postComment(questionID, parentCommentID, comment);

      const draftKey = getDraftKey(questionID, parentCommentID);
      localStorage.removeItem(draftKey);

      if (onSubmit) {
        onSubmit();
      } else {
        window.location.reload();
      }
    } catch (err) {
      setError(err);
    }

    setSubmitting(false);
    return false;
  };

  return (
    <>
      <div className="flex items-center pt-5 px-5">
        <Avatar
          className="comment-profile-img"
          name={currentUser?.displayName}
          src={currentUser?.profile_img}
          color="#f0f0f0"
          fgColor="#000000"
          size={40}
        />
        <div className="comment-profile-name">{currentUser?.displayName}</div>
      </div>
      {currentUser ? (
        <DraftEditor
          editorState={editorState}
          setEditorState={onEditorChange}
          editorRef={editorRef}
          showToolbar={showToolbar}
          placeholderText={placeholderText}
          onSubmit={handleSubmit}
          submitting={submitting}
        />
      ) : (
        <div>Login to comment</div>
      )}
      {error && (
        <div className="text-right text-red-600 text-sm pt-2 pb-4 px-4">
          {error.message}
        </div>
      )}
    </>
  );
};

export const CommentEditor = ({
  onOpen,
  isOpen = false,
  showToolbar = true,
  placeholderText = 'Add your own answer to this question...',
  questionID,
  parentCommentID,
  onSubmit,
  className,
}) => {
  const [open, setOpen] = useState(isOpen);

  useEffect(() => {
    const draftKey = getDraftKey(questionID, parentCommentID);
    const localStorageDraft = JSON.parse(localStorage.getItem(draftKey));
    if (localStorageDraft?.data) setOpen(true);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setOpen(true);
    }
  }, [isOpen]);

  const openEditor = () => {
    onOpen();
    setOpen(true);
  };

  return (
    <div className={cx('border border-gray-200 rounded-lg', className)}>
      {open ? (
        <OpenCommentEditor
          questionID={questionID}
          parentCommentID={parentCommentID}
          onSubmit={onSubmit}
          placeholderText={placeholderText}
          showToolbar={showToolbar}
        />
      ) : (
        <ClosedCommentEditor
          onOpen={openEditor}
          placeholderText={placeholderText}
        />
      )}
    </div>
  );
};

// Wrapper component mainly for extra ui components or
// special styling applied to the container if needed
export const ReplyEditor = ({
  questionID, parentCommentID, onSubmit, submitted,
}) => (submitted ? (
  <div className="p-4">
    Reply submitted! See the whole conversation
    {' '}
    <Link href={`/questions/${questionID}`} className="text-indigo-700">
      here
    </Link>
  </div>
) : (
  <div className="border-t border-gray-200">
    <OpenCommentEditor
      questionID={questionID}
      parentCommentID={parentCommentID}
      onSubmit={onSubmit}
      placeholderText="Add your reply..."
      showToolbar={false}
    />
  </div>
)
);

CommentEditor.propTypes = {
  questionID: PropTypes.string.isRequired,
  parentCommentID: PropTypes.string,
  showToolbar: PropTypes.bool,
  placeholderText: PropTypes.string,
};

ReplyEditor.propTypes = {
  questionID: PropTypes.string.isRequired,
  parentCommentID: PropTypes.string,
};
